import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useSubscription } from '@/hooks/useSubscription';
import { useRestaurant } from '@/hooks/useRestaurant';

interface UsageMetrics {
  branches: { current: number; limit: number; remaining: number };
  staff: { current: number; limit: number; remaining: number };
  menuItems: { current: number; limit: number; remaining: number };
  categories: { current: number; limit: number; remaining: number };
  monthlyOrders: { current: number; limit: number; remaining: number; monthName: string };
  isLoading: boolean;
  error: Error | null;
}

interface UsageLimitWarning {
  type: 'branches' | 'staff' | 'menuItems' | 'categories' | 'monthlyOrders';
  severity: 'info' | 'warning' | 'critical';
  message: string;
  percentage: number;
}

/**
 * Hook to get all usage metrics and limits for the current restaurant.
 * Combines data from multiple sources with subscription limits.
 */
export function useUsageMetrics(): UsageMetrics & { warnings: UsageLimitWarning[] } {
  const { restaurant } = useRestaurant();
  const { subscription } = useSubscription();
  const restaurantId = restaurant?.id;

  // Get current month boundaries
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  const monthName = now.toLocaleString('default', { month: 'long', year: 'numeric' });

  // Fetch all usage counts in parallel
  const { data: usageData, isLoading, error } = useQuery({
    queryKey: ['usage-metrics', restaurantId, now.getMonth(), now.getFullYear()],
    queryFn: async () => {
      if (!restaurantId) return null;

      // Fetch all counts in parallel
      const [branchesResult, staffResult, categoriesResult, menuItemsResult, ordersResult] = await Promise.all([
        // Branches count
        supabase.from('branches').select('*', { count: 'exact', head: true }).eq('restaurant_id', restaurantId),
        // Staff count
        supabase.from('staff_members').select('*', { count: 'exact', head: true }).eq('restaurant_id', restaurantId),
        // Categories count (across all branches)
        supabase.from('categories').select('*, branches!inner(restaurant_id)', { count: 'exact', head: true }).eq('branches.restaurant_id', restaurantId),
        // Menu items count
        supabase.from('menu_items').select('*, categories!inner(branch_id, branches!inner(restaurant_id))', { count: 'exact', head: true }).eq('categories.branches.restaurant_id', restaurantId),
        // Monthly orders count
        (async () => {
          const { data: branches } = await supabase.from('branches').select('id').eq('restaurant_id', restaurantId);
          if (!branches?.length) return { count: 0 };
          const branchIds = branches.map(b => b.id);
          return supabase.from('orders').select('*', { count: 'exact', head: true })
            .in('branch_id', branchIds)
            .gte('created_at', startOfMonth.toISOString())
            .lte('created_at', endOfMonth.toISOString());
        })()
      ]);

      return {
        branches: branchesResult.count || 0,
        staff: staffResult.count || 0,
        categories: categoriesResult.count || 0,
        menuItems: menuItemsResult.count || 0,
        monthlyOrders: ordersResult.count || 0,
      };
    },
    enabled: !!restaurantId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });

  // Get limits from subscription
  const limits = {
    branches: (subscription?.features?.max_branches as number) || Infinity,
    staff: (subscription?.features?.max_staff as number) || Infinity,
    menuItems: (subscription?.features?.max_menu_items as number) || Infinity,
    categories: (subscription?.features?.max_categories as number) || Infinity,
    monthlyOrders: (subscription?.features?.monthly_orders_limit as number) || Infinity,
  };

  const current = {
    branches: usageData?.branches || 0,
    staff: usageData?.staff || 0,
    menuItems: usageData?.menuItems || 0,
    categories: usageData?.categories || 0,
    monthlyOrders: usageData?.monthlyOrders || 0,
  };

  // Calculate remaining and generate warnings
  const warnings: UsageLimitWarning[] = [];
  
  const calculateWarning = (
    type: UsageLimitWarning['type'],
    current: number,
    limit: number
  ): UsageLimitWarning | null => {
    if (limit === Infinity || limit === 0) return null;
    
    const percentage = (current / limit) * 100;
    
    if (percentage >= 100) {
      return {
        type,
        severity: 'critical',
        message: `You have reached your ${type.replace(/([A-Z])/g, ' $1').toLowerCase()} limit`,
        percentage,
      };
    } else if (percentage >= 80) {
      return {
        type,
        severity: 'warning',
        message: `You are using ${percentage.toFixed(0)}% of your ${type.replace(/([A-Z])/g, ' $1').toLowerCase()} limit`,
        percentage,
      };
    } else if (percentage >= 60) {
      return {
        type,
        severity: 'info',
        message: `You are using ${percentage.toFixed(0)}% of your ${type.replace(/([A-Z])/g, ' $1').toLowerCase()} limit`,
        percentage,
      };
    }
    return null;
  };

  // Check each resource for warnings
  const branchWarning = calculateWarning('branches', current.branches, limits.branches);
  if (branchWarning) warnings.push(branchWarning);
  
  const staffWarning = calculateWarning('staff', current.staff, limits.staff);
  if (staffWarning) warnings.push(staffWarning);
  
  const menuItemsWarning = calculateWarning('menuItems', current.menuItems, limits.menuItems);
  if (menuItemsWarning) warnings.push(menuItemsWarning);
  
  const categoriesWarning = calculateWarning('categories', current.categories, limits.categories);
  if (categoriesWarning) warnings.push(categoriesWarning);
  
  const ordersWarning = calculateWarning('monthlyOrders', current.monthlyOrders, limits.monthlyOrders);
  if (ordersWarning) warnings.push(ordersWarning);

  return {
    branches: {
      current: current.branches,
      limit: limits.branches,
      remaining: Math.max(0, limits.branches - current.branches),
    },
    staff: {
      current: current.staff,
      limit: limits.staff,
      remaining: Math.max(0, limits.staff - current.staff),
    },
    menuItems: {
      current: current.menuItems,
      limit: limits.menuItems,
      remaining: Math.max(0, limits.menuItems - current.menuItems),
    },
    categories: {
      current: current.categories,
      limit: limits.categories,
      remaining: Math.max(0, limits.categories - current.categories),
    },
    monthlyOrders: {
      current: current.monthlyOrders,
      limit: limits.monthlyOrders,
      remaining: Math.max(0, limits.monthlyOrders - current.monthlyOrders),
      monthName,
    },
    isLoading,
    error: error as Error | null,
    warnings,
  };
}

/**
 * Simple hook to check if monthly orders limit is reached.
 * Useful for quick checks before order creation.
 */
export function useCanCreateOrder(branchId: string): { canCreate: boolean; isLoading: boolean; message?: string } {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);

  const { data, isLoading } = useQuery({
    queryKey: ['can-create-order', branchId, now.getMonth(), now.getFullYear()],
    queryFn: async () => {
      if (!branchId) return { canCreate: true, restaurantId: null };

      // Get restaurant ID from branch
      const { data: branch, error: branchError } = await supabase
        .from('branches')
        .select('restaurant_id')
        .eq('id', branchId)
        .single();

      if (branchError || !branch) {
        return { canCreate: true, restaurantId: null };
      }

      // Get subscription for this restaurant
      const { data: subscription } = await supabase
        .from('tenant_subscriptions')
        .select('cached_features')
        .eq('restaurant_id', branch.restaurant_id)
        .single();

      if (!subscription?.cached_features) {
        return { canCreate: true, restaurantId: branch.restaurant_id };
      }

      const features = subscription.cached_features as Record<string, unknown>;
      const monthlyLimit = (features.monthly_orders_limit as number) || 0;

      // 0 or very high number means unlimited
      if (monthlyLimit === 0 || monthlyLimit >= 999999) {
        return { canCreate: true, restaurantId: branch.restaurant_id };
      }

      // Count orders this month for all branches of this restaurant
      const { data: allBranches } = await supabase
        .from('branches')
        .select('id')
        .eq('restaurant_id', branch.restaurant_id);

      if (!allBranches?.length) {
        return { canCreate: true, restaurantId: branch.restaurant_id };
      }

      const branchIds = allBranches.map(b => b.id);
      const { count } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true })
        .in('branch_id', branchIds)
        .gte('created_at', startOfMonth.toISOString())
        .lte('created_at', endOfMonth.toISOString());

      const currentCount = count || 0;
      return {
        canCreate: currentCount < monthlyLimit,
        restaurantId: branch.restaurant_id,
        currentCount,
        limit: monthlyLimit,
      };
    },
    enabled: !!branchId,
    staleTime: 60 * 1000, // 1 minute - orders change frequently
  });

  if (isLoading) {
    return { canCreate: true, isLoading: true }; // Allow while loading
  }

  if (!data?.canCreate && data?.limit) {
    return {
      canCreate: false,
      isLoading: false,
      message: `This restaurant has reached its monthly order limit (${data.currentCount}/${data.limit}). Please contact the restaurant.`,
    };
  }

  return { canCreate: true, isLoading: false };
}

/**
 * Hook to check if online ordering is enabled based on subscription tier.
 * Returns the ordering tier level for conditional feature display.
 * 
 * IMPORTANT: This hook follows a "fail-open" approach - ordering is ALWAYS 
 * enabled by default unless EXPLICITLY disabled by subscription settings.
 * The menu should always work; subscription only controls specific features.
 */
export function useOnlineOrderingTier(branchId: string): {
  isEnabled: boolean;
  tier: 'none' | 'basic' | 'advanced' | 'enterprise';
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: ['online-ordering-tier', branchId],
    queryFn: async () => {
      // Default: always allow ordering (fail-open)
      const defaultResult = { isEnabled: true, tier: 'basic' as const };
      
      if (!branchId) return defaultResult;

      // Get restaurant ID from branch
      const { data: branch, error: branchError } = await supabase
        .from('branches')
        .select('restaurant_id, enable_ordering')
        .eq('id', branchId)
        .single();

      // If we can't read the branch, still allow ordering (fail-open)
      if (branchError || !branch) {
        return defaultResult;
      }

      // If branch itself has ordering disabled by the restaurant owner, respect that
      if (!branch.enable_ordering) {
        return { isEnabled: false, tier: 'none' as const };
      }

      // Try to get subscription for this restaurant
      const { data: subscription, error: subscriptionError } = await supabase
        .from('tenant_subscriptions')
        .select('cached_features, status')
        .eq('restaurant_id', branch.restaurant_id)
        .in('status', ['active', 'trialing'])
        .maybeSingle();

      // If subscription read fails or no subscription exists, allow ordering (fail-open)
      // This ensures the menu works for new restaurants without subscriptions
      if (subscriptionError || !subscription) {
        return defaultResult;
      }

      // If no features cached, default to allowing ordering
      if (!subscription.cached_features) {
        return defaultResult;
      }

      const features = subscription.cached_features as Record<string, unknown>;
      
      // ONLY disable if explicitly set to false in subscription
      const moduleEnabled = features.module_online_ordering;
      if (moduleEnabled === false) {
        return { isEnabled: false, tier: 'none' as const };
      }

      // Get the tier level (default to basic if not set)
      const tier = (features.online_ordering_tier as string) || 'basic';
      const validTiers = ['none', 'basic', 'advanced', 'enterprise'];
      const validatedTier = validTiers.includes(tier) ? tier : 'basic';

      // Only disable if tier is explicitly 'none'
      if (validatedTier === 'none') {
        return { isEnabled: false, tier: 'none' as const };
      }

      return { 
        isEnabled: true, 
        tier: validatedTier as 'none' | 'basic' | 'advanced' | 'enterprise'
      };
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000, // 5 minutes - subscription rarely changes
  });

  // While loading OR on error, default to allowing ordering (fail-open)
  if (isLoading || !data) {
    return { isEnabled: true, tier: 'basic', isLoading };
  }

  return { 
    isEnabled: data.isEnabled, 
    tier: data.tier, 
    isLoading: false 
  };
}

/**
 * Hook to check if data export is enabled for the current subscription.
 * Returns true if the user can export data (CSV/Excel), false otherwise.
 * 
 * Basic plan: false (no export)
 * Pro/Enterprise/Trial: true (export enabled)
 */
export function useCanExportData(): {
  canExport: boolean;
  isLoading: boolean;
} {
  const { subscription, isLoading } = useSubscription();

  // While loading, assume export is allowed to prevent blocking UI
  if (isLoading) {
    return { canExport: true, isLoading: true };
  }

  // If no subscription, default to allowing export (fail-open for new tenants)
  if (!subscription?.features) {
    return { canExport: true, isLoading: false };
  }

  // Check the data_export feature
  const dataExportEnabled = subscription.features.data_export;
  
  // If explicitly false, export is disabled
  if (dataExportEnabled === false) {
    return { canExport: false, isLoading: false };
  }

  // Otherwise, allow export
  return { canExport: true, isLoading: false };
}
